import type { Jsonified } from "@redotech/json/json";
import { createEnum } from "@redotech/zod-util/zod-enum";
import type { ObjectId } from "bson";
import * as z from "zod/v4";
import { ConversationInsights } from "./conversation-insights";
import type { ConversationTagSource } from "./conversation-tags/conversation-tag-record";
import type { ConversationMessage as ConversationMessageSchema } from "./conversation/conversation-message-db-parser";
import {
  AuditLogDbParser,
  SMSKind,
} from "./conversation/conversation-schema-db-parser";
import { Customer } from "./customer";
import type { PillTheme } from "./pill-theme";
import type { EmailReplyType } from "./support/conversations/email-info";
import { TelnyxErrorCode } from "./text-messaging/telnyx-types";

export interface ConversationTag {
  name: string;
  pillTheme?: PillTheme;
}

export interface ConversationTagWithId {
  tagId: string;
  name: string;
  pillTheme: PillTheme;
  source: ConversationTagSource;
  tagGroupId?: string;
}

export interface BackendConversationFile {
  _id?: ObjectId;
  url: string;
  description?: string | null;
  mimeType?: string | null;
  originalContentId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ConversationFile {
  _id: string;
  url: string;
  description?: string | undefined;
  mimeType?: string | undefined;
  originalContentId?: string | undefined;
  createdAt: string;
  updatedAt: string;
}

export interface InstagramCommentInfo {
  commentId?: string;
  mediaId?: string;
  username?: string;
  igScopedUserId?: string;
  hasBeenRepliedTo?: boolean;
  state?: CommentState;
  liked?: boolean;
}

export enum CommentState {
  NORMAL = "normal",
  DELETED = "deleted",
  HIDDEN = "hidden",
  EDITED = "edited",
}

export interface FacebookCommentInfo {
  commentId?: string;
  postId?: string;
  facebookName?: string;
  pageScopedId?: string;
  hasBeenRepliedTo?: boolean;
  deleted?: boolean;
  state?: CommentState;
  liked?: boolean;
}

export const textForDeletedFacebookComment = "<Message deleted in Facebook>";

export interface InstagramCommentThreadInfo {
  topLevelCommentId: string;
  mediaId: string;
  mediaPreviewUrl?: string;
  permalink?: string;
  commentPermalink?: string;
  redoMessageIdOfRepliedComment?: string;
  redoConversationIdOfRepliedComment?: string;
}

export interface FacebookCommentThreadInfo {
  topLevelCommentId: string;
  postId: string;
  permalink: string;
  mediaPreviewUrl?: string;
  redoMessageIdOfRepliedComment?: string;
  redoConversationIdOfRepliedComment?: string;
}

export interface ConversationMessageEmailData {
  textBody: string;
  htmlBody: string;
  messageId?: string;
  gmailMessageId: string;
  outlookMessageId: string;
  from: { email: string; name: string };
  to: [{ email: string; name: string }];
  cc?: [{ email?: string; name?: string }];
  bcc?: [{ email?: string; name?: string }];
  autoSubmitted?: string;
  suppressAutoReplies?: SuppressAutoRepliesValue[];
}

export interface ConversationEmailData {
  kind: string;
  email: string;
  gmailThreadId: string;
  outlookConversationId: string;
}

export interface ConversationMessageInstagramData {
  messageId?: string;
  repliedStoryUrl?: string;
  mentionedStoryUrl?: string;
  comment?: InstagramCommentInfo;
  privateRepliedComment?: {
    commentId: string;
    commentText: string;
    permalink?: string;
  };
}

export interface ConversationMessageFacebookData {
  messageId?: string;
  comment?: FacebookCommentInfo;
  privateRepliedComment?: {
    commentId: string;
    commentText: string;
    permalink?: string;
  };
}

export const conversationMessageTextMessageType = [
  "SMS",
  "MMS",
  "iMessage",
  // Written by the iMessage gateway consumer (imessage-gateway-consumer/src/index.ts)
  // when an outbound iMessage falls back to Telnyx SMS. Must be accepted here so
  // downstream reads of the support Conversation parse — otherwise every later
  // fetch of this conversation (including concierge sends) fails at Zod.
  "smsFallback",
] as const;
export type ConversationTextMessageType =
  (typeof conversationMessageTextMessageType)[number];

export const suppressAutoRepliesValue = [
  "ndr", // non-delivery receipts
  "rn", // read notifications
  "nrn", // non-read notifications
  "oof", // out-of-office replies
  "autoreply", // automatic replies
  "all", // all automatic responses
] as const;
export type SuppressAutoRepliesValue =
  (typeof suppressAutoRepliesValue)[number];

export interface ConversationMessageTextMessageData {
  messageId?: string;
  kind?: ConversationTextMessageType;
  providerErrors?: TelnyxErrorCode[];
}

export interface ConversationMessageTiktokData {
  messageIds?: string[];
}

export enum MessageVisibility {
  PUBLIC = "public",
  INTERNAL = "internal",
}

export interface ConversationMessageDraftInfo {
  emailDraftInfo?: {
    inReplyTo?: string;
    replyType: EmailReplyType;
    draftHtmlBody?: string;
  };
  isDraft: boolean;
  inReplyToMongoId?: string;
}

export interface ConversationMessageDocument {
  _id: ObjectId;
  customer?: ConversationCustomer;
  user: ObjectId;
  type: ConversationMessageType;
  callRecord: ObjectId;
  visibility: MessageVisibility;
  content: string;
  structuredContent?: any;
  contentType?: ContentType;
  actionType?: ActionType;
  intent?: IntentType;
  actionableContent?: Record<string, any>;
  customerInputContent?: Record<string, any>;
  toolCallId?: string;
  systemPromptId?: string;
  chatFlow?: { htmlContent?: string };
  sentAt: Date;
  createdAt: Date;
  updatedAt: Date;
  files: [ConversationFile];
  email?: ConversationMessageEmailData;
  instagram?: ConversationMessageInstagramData;
  facebook?: ConversationMessageFacebookData;
  tiktok?: ConversationMessageTiktokData;
  externalOrigin?: boolean;
  orderList?: boolean;
  aiGenerations?: { text: string }[];
  aiMerchantApproval?: AiMerchantApproval;
  aiMerchantApprovalReasoning?: string;
  aiMerchantFeedbackUpdatedAt?: Date;
  textMessage?: ConversationMessageTextMessageData;
  /** a developer (not merchant or customer) friendly error message indicating a sending issue with the message */
  error?: string;
  draftInfo?: ConversationMessageDraftInfo;
  usersMentioned?: string[];
  cancelledByUser?: boolean;
}

export type ConversationMessage = Jsonified<ConversationMessageDocument>;
export type ExpandedConversationMessageDoc = Omit<
  ConversationMessageDocument,
  "user"
> & { user: any };

/** Same as ConversationMessages except that user is of type any */
export type ExpandedConversationMessage =
  Jsonified<ExpandedConversationMessageDoc>;

export enum ConversationPlatform {
  EMAIL = "email",
  REDO_CHAT = "redoChat",
  FACEBOOK = "facebook",
  FACEBOOK_COMMENTS = "facebookComments",
  INSTAGRAM = "instagram",
  INSTAGRAM_COMMENTS = "instagramComments",
  SMS = "sms",
  VOICE = "voice",
  ATTENTIVE = "attentive",
  POSTSCRIPT = "postscript",
  TIKTOK = "tiktok",
  MOBILE_MESSAGE = "mobileMessage",
}

export const doesPlatformUseHtmlContent: Record<ConversationPlatform, boolean> =
  {
    [ConversationPlatform.EMAIL]: true,
    [ConversationPlatform.REDO_CHAT]: false,
    [ConversationPlatform.FACEBOOK]: false,
    [ConversationPlatform.FACEBOOK_COMMENTS]: false,
    [ConversationPlatform.INSTAGRAM]: false,
    [ConversationPlatform.INSTAGRAM_COMMENTS]: false,
    [ConversationPlatform.SMS]: false,
    [ConversationPlatform.VOICE]: false,
    [ConversationPlatform.ATTENTIVE]: false,
    [ConversationPlatform.POSTSCRIPT]: false,
    [ConversationPlatform.TIKTOK]: false,
    [ConversationPlatform.MOBILE_MESSAGE]: false,
  };

export const ConversationPlatformString: Record<ConversationPlatform, string> =
  {
    [ConversationPlatform.EMAIL]: "Email",
    [ConversationPlatform.REDO_CHAT]: "Chat",
    [ConversationPlatform.FACEBOOK]: "Facebook Messenger",
    [ConversationPlatform.FACEBOOK_COMMENTS]: "Facebook comments",
    [ConversationPlatform.INSTAGRAM]: "Instagram DM",
    [ConversationPlatform.INSTAGRAM_COMMENTS]: "Instagram comments",
    [ConversationPlatform.SMS]: "SMS",
    [ConversationPlatform.VOICE]: "Voice",
    [ConversationPlatform.ATTENTIVE]: "Attentive",
    [ConversationPlatform.POSTSCRIPT]: "Postscript",
    [ConversationPlatform.TIKTOK]: "TikTok",
    [ConversationPlatform.MOBILE_MESSAGE]: "iMessage",
  };

export enum ConversationStatus {
  OPEN = "open",
  CLOSED = "closed",
  SNOOZED = "snoozed",
  IN_PROGRESS = "in_progress",
  HIDDEN = "hidden",
}
export const ConversationStatusSchema = createEnum(ConversationStatus, {
  enumName: "ConversationStatus",
});

function isSnoozed(snoozedUntil: string | undefined): boolean {
  if (snoozedUntil) {
    const snoozeDate = new Date(snoozedUntil);
    const now = new Date();
    return now < snoozeDate;
  } else {
    return false;
  }
}

export function getConversationStatus(conversation: MinimalConversation) {
  if (isSnoozed(conversation.snoozedUntil)) {
    return ConversationStatus.SNOOZED;
  }
  switch (conversation.status) {
    case "closed":
      return ConversationStatus.CLOSED;
    case "in_progress":
      return ConversationStatus.IN_PROGRESS;
    case "open":
      return ConversationStatus.OPEN;
    default:
      return ConversationStatus.OPEN;
  }
}

export enum ConversationMessageType {
  CUSTOMER = "customer",
  MERCHANT = "merchant",
  SYSTEM = "system",
  CALL = "call",
  TOOL = "tool",
}

export enum ActionType {
  READ = "READ",
  CREATE = "CREATE",
  UNSPECIFIED = "UNSPECIFIED",
}

export enum IntentType {
  RECOMMEND = "RECOMMEND",
  PURCHASE = "PURCHASE",
}

export enum TrackOrderUiState {
  ORDER_LOOKUP = "ORDER_LOOKUP",
  FOUND_ORDER = "FOUND_ORDER",
}

export enum InputStepType {
  DEFAULT = "default",
  COLLECT_EMAIL = "collect_email",
  CODE_VERIFICATION = "code_verification",
  OFFERING_CONVERT_TO_EMAIL = "offering_convert_to_email",
}

export enum ConversationMode {
  MERCHANT_DASHBOARD_DEMO = "MERCHANT_DASHBOARD_DEMO",
}

export enum ResponseLength {
  EXTREMELY_BRIEF = "EXTREMELY_BRIEF",
  CONCISE = "CONCISE",
  MODERATE = "MODERATE",
  DETAILED = "DETAILED",
  COMPREHENSIVE = "COMPREHENSIVE",
}

export enum ContentType {
  TOOL_CALL = "TOOL_CALL",
  TEXT = "TEXT",
  CART_ITEMS = "CART_ITEMS",
  CANCEL_ORDER = "CANCEL_ORDER",
  PRODUCT_DISPLAY = "PRODUCT_DISPLAY",
  PRODUCT_COMPARISON = "PRODUCT_COMPARISON",
  DISPLAY_ORDER_TRACKING_INFO = "DISPLAY_ORDER_TRACKING_INFO",
  TRACK_ORDER_UI = "TRACK_ORDER_UI",
  TOOL_CALL_PRODUCT_INFO = "PRODUCTS",
  TOOL_CALL_RELEVANT_PRODUCTS = "RELEVANT_PRODUCTS",
  SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION = "SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION",
  SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION_UI = "SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION_UI",
  CONTACT_SUPPORT_UI = "CONTACT_SUPPORT_UI",
  EMAIL_INPUT = "EMAIL_INPUT",
  DISCOUNT_CODE = "DISCOUNT_CODE",
  RESEND_RETURN_LABEL_UI = "RESEND_RETURN_LABEL_UI",
  TOOL_CALL_TRACK_ORDER_AVAILABLE = "TOOL_CALL_TRACK_ORDER_AVAILABLE",
  TOOL_CALL_ERROR = "TOOL_CALL_ERROR",
  UPDATE_ADDRESS = "UPDATE_ADDRESS",
  TOOL_CONFIRMATION_PROMPT = "TOOL_CONFIRMATION_PROMPT",
  UPDATE_ORDER = "UPDATE_ORDER",
  CREATE_BUNDLE = "CREATE_BUNDLE",
  PRODUCT_VISUALIZATION_UPLOAD_UI = "PRODUCT_VISUALIZATION_UPLOAD_UI",
  PRODUCT_VISUALIZATION_RESULT = "PRODUCT_VISUALIZATION_RESULT",
  FAQ_LINK = "FAQ_LINK",
  WISHLIST_DISPLAY = "WISHLIST_DISPLAY",
  CUSTOMER_SIGN_IN_UI = "CUSTOMER_SIGN_IN_UI",
}

export enum OrderAvailableToTrack {
  ORDER_AVAILABLE_TO_TRACK = "ORDER_AVAILABLE_TO_TRACK",
  NO_ORDER_FOUND = "NO_ORDER_FOUND",
  ORDER_NOT_TRACKABLE = "ORDER_NOT_TRACKABLE",
}

export enum ComponentConfirmationState {
  PENDING = "pending",
  CONFIRMED = "confirmed",
  REJECTED = "rejected",
}

export enum AiMerchantApproval {
  APPROVED = "approved",
  DISAPPROVED = "disapproved",
  SKIPPED = "skipped",
  NO_STATUS = "no status",
}

export interface CustomerSupportCommunicationConsent {
  textMessages: { optInDate?: Date | null; optOutDate?: Date | null };
}

export interface ConversationCustomer extends Customer {
  customer: string;
}

export interface ConversationSms {
  phoneNumber?: string;
  merchantPhoneNumber?: string;
  kind?: SMSKind;
}

export type AuditLog = typeof AuditLogDbParser;

export interface AIInformation {
  version?: number;
  categories?: string[];
  analytics?: {
    productInteractions?: {
      productId: string;
      variantId?: string;
      action: string;
      timestamp: Date;
    }[];
    suggestedProducts?: { productId: string; timestamp?: Date }[];
    productsAddedToCart?: {
      productId: string;
      variantId?: string;
      quantity: number;
      timestamp?: Date;
    }[];
    backInStockSubscriptions?: {
      productId: string;
      variantId?: string;
      timestamp?: Date;
    }[];
  };
  orders?: string[];
  shopifyOrders?: string[];
  auditLog?: AuditLog[];
}

export const AiAutopilotAddedToBilling = [
  "freeTrial",
  "billed",
  "notBilled",
] as const;
export type AiAutopilotAddedToBilling =
  (typeof AiAutopilotAddedToBilling)[number];

export interface ConversationDocument {
  _id: string;
  emailMessageId: string;
  ticketNumber: number;
  platform: ConversationPlatform;
  originalPlatform?: ConversationPlatform;
  email?: ConversationEmailData;
  team: any;
  aiBillable: boolean;
  aiBillableCheckedAt: Date;
  partOfConciergeMigration?: boolean;
  conciergeMigratedConversationId?: string;
  aiAutopilotAddedToBilling: AiAutopilotAddedToBilling;
  aiResolutionPriceInCents?: number;
  customer: ConversationCustomer;
  assignee: any;
  status: string;
  lastResponseAt: Date;
  lastCustomerResponseAt: Date;
  lastMerchantResponseAt?: Date;
  lastKnownStepId?: number | null;
  inputStepType?: InputStepType;
  fourDigitCodeId?: string;
  snoozedUntil?: Date;
  cameFromContactForm?: boolean;
  expiredAt?: Date;
  tagIds?: [ConversationTagWithId];
  files: [ConversationFile];
  messages: ConversationMessage[];
  createdAt: Date;
  updatedAt: Date;
  closedAt: Date;
  subject: string;
  sms?: ConversationSms;
  ccEmails?: [string];
  bccEmails?: [string];
  toEmails?: [{ email: string; name: string }];
  archived: boolean;
  archivedAt: Date;
  toObject?(...args: unknown[]): unknown;
  carts: string[];
  embedding: number[];
  offeringConvertToEmail?: boolean; // undefined means not offered, true means currently offering, false means previously offered
  instagramCommentThread?: InstagramCommentThreadInfo;
  facebookCommentThread?: FacebookCommentThreadInfo;
  currentAiResponse: string;
  read?: boolean;
  metaIntegrationId?: string;
  metaPageName?: string;
  externalConversationId?: string;
  conversationInsights?: ConversationInsights;
}

export interface MessageInfo {
  content: string;
  files?: ConversationFile[];
  chatFlow?: { htmlContent?: string };
  contentType?: ContentType;
  type: string;
  sentAt: string;
  orderList?: boolean;
  draftInfo?: { isDraft: boolean };
  user?: string | { _id: string; name: string };
}

export interface ChatInfo {
  token?: string;
  conversationId?: string;
  csatSurveyCollected?: boolean;
  csatRating?: number;
  csatComment?: string;
  csatEditExpiresAt?: string; // ISO string
  messages?: MessageInfo[];
  currentChatFlowStep?: number | null;
  chatClosed?: boolean;
  disallowFutureMessages?: boolean;
  teamId?: string;
  newChat?: boolean;
  isConvertedFromConcierge?: boolean;
  collectEmail?: boolean;
  fourDigitCodeId?: string;
  email?: string;
  offeringConvertToEmail?: boolean;
}

type MinimalDraftInfoDocument = Pick<
  ConversationMessageDraftInfo,
  "isDraft" | "inReplyToMongoId"
>;

type MinimalConversationMessageDocument = Pick<
  ConversationMessageDocument,
  "_id" | "user" | "type" | "sentAt" | "error" | "visibility"
> & { draftInfo?: MinimalDraftInfoDocument };

export type MinimalConversationMessageWithContentDocument =
  MinimalConversationMessageDocument & {
    content: string;
    facebook?: ConversationMessageFacebookData;
  };

export type MinimalConversationMessage =
  Jsonified<MinimalConversationMessageDocument>;

export type MinimalConversationMessageWithContent =
  Jsonified<MinimalConversationMessageWithContentDocument>;

export type MinimalConversationDocument = Pick<
  ConversationDocument,
  | "_id"
  | "team"
  | "platform"
  | "originalPlatform"
  | "updatedAt"
  | "subject"
  | "lastResponseAt"
  | "customer"
  | "status"
  | "read"
  | "tagIds"
  | "assignee"
  | "metaPageName"
  | "ticketNumber"
  | "snoozedUntil"
  | "instagramCommentThread"
  | "facebookCommentThread"
  | "createdAt"
  | "carts"
  | "conversationInsights"
> & { messages: MinimalConversationMessageDocument[] } & {
  lastMessage?: MinimalConversationMessageWithContentDocument;
  latestDraftMessage?: MinimalConversationMessageWithContentDocument;
};

export type Conversation = Jsonified<ConversationDocument>;
export type MinimalConversation = Jsonified<MinimalConversationDocument>;

/** Same as Conversation except that message.user is of type any */
export type ExpandedConversation = Omit<Conversation, "messages"> & {
  messages: ExpandedConversationMessage[];
};

function convertToMinimalBase<T extends ConversationDocument | Conversation>(
  conversation: T,
): T extends ConversationDocument
  ? MinimalConversationDocument
  : MinimalConversation {
  return {
    _id: conversation._id,
    team: conversation.team,
    platform: conversation.platform,
    originalPlatform: conversation.originalPlatform,
    updatedAt: conversation.updatedAt,
    lastResponseAt: conversation.lastResponseAt,
    customer: conversation.customer,
    subject: conversation.subject,
    status: conversation.status,
    read: conversation.read,
    tagIds: conversation.tagIds,
    assignee: conversation.assignee,
    snoozedUntil: conversation.snoozedUntil,
    metaPageName: conversation.metaPageName,
    ticketNumber: conversation.ticketNumber,
    carts: conversation.carts,
    instagramCommentThread: conversation.instagramCommentThread,
    facebookCommentThread: conversation.facebookCommentThread,
    createdAt: conversation.createdAt,
    messages: conversation.messages.map(convertToMinimalConversationMessage),
    lastMessage: convertToMinimalConversationMessageWithContent(
      conversation.messages[conversation.messages.length - 1],
    ),
    latestDraftMessage: convertToMinimalConversationMessageWithContent(
      conversation.messages.find((message) => message.draftInfo?.isDraft),
    ),
    conversationInsights: conversation.conversationInsights,
  } as T extends ConversationDocument
    ? MinimalConversationDocument
    : MinimalConversation;
}

export function convertToMinimalConversationDocument(
  conversation: ConversationDocument,
): MinimalConversationDocument {
  return convertToMinimalBase(conversation);
}

export function convertToMinimalConversation(
  conversation: Conversation,
): MinimalConversation {
  return convertToMinimalBase(conversation);
}

function convertToMinimalConversationMessage<
  T extends ConversationMessage | ConversationMessageDocument,
>(
  message: T,
): T extends ConversationMessageDocument
  ? MinimalConversationMessageDocument
  : MinimalConversationMessage {
  return {
    _id: message._id,
    user: message.user,
    type: message.type,
    sentAt: message.sentAt,
    draftInfo: message.draftInfo
      ? {
          isDraft: message.draftInfo.isDraft,
          inReplyToMongoId: message.draftInfo.inReplyToMongoId,
        }
      : undefined,
    error: message.error,
    visibility: message.visibility,
  } as T extends ConversationMessageDocument
    ? MinimalConversationMessageDocument
    : MinimalConversationMessage;
}

function convertToMinimalConversationMessageWithContent<
  T extends ConversationMessage | ConversationMessageDocument,
>(
  message: T | undefined,
):
  | (T extends ConversationMessageDocument
      ? MinimalConversationMessageWithContentDocument
      : MinimalConversationMessageWithContent)
  | undefined {
  if (!message) {
    return undefined;
  }
  return {
    ...convertToMinimalConversationMessage(message),
    content: message.content,
    facebook: message.facebook,
  } as T extends ConversationMessageDocument
    ? MinimalConversationMessageWithContentDocument
    : MinimalConversationMessageWithContent;
}

export function isExpandedConversation(
  conversation: ExpandedConversation | MinimalConversation,
): conversation is ExpandedConversation {
  return conversation.messages.some((message) =>
    Object.prototype.hasOwnProperty.call(message, "content"),
  );
}

/**
 * This string is used as a system message, and as a logical condition to make the UI show a special case of
 * redochat -> email conversion.
 * Changing this will inhibit old chat->email conversion UI from showing the special case,
 * but won't break the ticket conversion UI entirely.
 */
export const TICKET_CONVERTED_FROM_REDOCHAT_TO_EMAIL_MESSAGE =
  "Ticket converted from RedoChat to email";

export const TICKET_CONVERTED_FROM_CONCIERGE_TO_EMAIL_MESSAGE =
  "AI conversation converted to email support. A team member will be reaching out shortly.";

const LEGACY_TICKET_CONVERTED_FROM_CONCIERGE_TO_EMAIL_MESSAGE =
  "Concierge conversation converted to email support";

export const TICKET_CONVERTED_FROM_AI_LIVE_CHAT_TO_SUPPORT_LIVE_CHAT =
  "Conversation converted to support live chat";

export const TICKET_CONVERTED_TO_EXTERNAL_EMAIL_MESSAGE =
  "Conversation converted to external email";

export const TICKET_CONVERTED_FROM_VOICE_TO_EMAIL_MESSAGE =
  "Ticket converted from Voice to email";

export const TICKET_CONVERTED_FROM_VOICE_TO_SMS_MESSAGE =
  "Ticket converted from Voice to SMS";

export type ConversionMessage =
  | typeof TICKET_CONVERTED_FROM_REDOCHAT_TO_EMAIL_MESSAGE
  | typeof TICKET_CONVERTED_FROM_CONCIERGE_TO_EMAIL_MESSAGE
  | typeof LEGACY_TICKET_CONVERTED_FROM_CONCIERGE_TO_EMAIL_MESSAGE
  | typeof TICKET_CONVERTED_FROM_AI_LIVE_CHAT_TO_SUPPORT_LIVE_CHAT
  | typeof TICKET_CONVERTED_FROM_VOICE_TO_EMAIL_MESSAGE
  | typeof TICKET_CONVERTED_FROM_VOICE_TO_SMS_MESSAGE;

export const findLastConvertedMessage = (messages: ConversationMessage[]) => {
  return messages
    .slice()
    .reverse()
    .find(
      (message) =>
        (message.content === TICKET_CONVERTED_FROM_REDOCHAT_TO_EMAIL_MESSAGE ||
          message.content ===
            TICKET_CONVERTED_FROM_CONCIERGE_TO_EMAIL_MESSAGE ||
          LEGACY_TICKET_CONVERTED_FROM_CONCIERGE_TO_EMAIL_MESSAGE) &&
        message.type === "system",
    );
};

export const isSupportOrEmailConversionMessage = (
  message: ConversationMessage | ConversationMessageSchema,
) => {
  return (
    "content" in message &&
    (message.content === TICKET_CONVERTED_FROM_REDOCHAT_TO_EMAIL_MESSAGE ||
      message.content === TICKET_CONVERTED_FROM_CONCIERGE_TO_EMAIL_MESSAGE ||
      message.content ===
        LEGACY_TICKET_CONVERTED_FROM_CONCIERGE_TO_EMAIL_MESSAGE ||
      message.content ===
        TICKET_CONVERTED_FROM_AI_LIVE_CHAT_TO_SUPPORT_LIVE_CHAT)
  );
};

export const TICKET_EXPIRED_MESSAGE = "Conversation expired and closed";

export const UNRESPONSIVE_CONVERSION_PROMPT =
  "We apologize for the delay in our response. Please provide your email address so we can follow up with you.";

export interface ConversationCategory {
  category: string;
  count: number;
}
export const conversationCategorySchema = z.object({
  category: z.string(),
  count: z.number(),
});

export const CONVERSATION_CATEGORIES = [
  "Order status",
  "Returns or exchanges",
  "Stock availability",
  "Shipping information",
  "Size and fit",
  "Style",
  "Warranty information",
  "General product question",
] as const;

export type ConversationCategoryType = (typeof CONVERSATION_CATEGORIES)[number];
